import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setExtractedData, setEditableData, setValidationErrors, clearValidationErrors, setDeclarationDirty, clearSelectedTemplate, setSelectedTemplate } from '../store/reducers/index';
import { useLanguage } from './useLanguage';

export const useDeclarationSummary = (jsonData) => {
  const dispatch = useDispatch();
  const extractedData = useSelector(state => state.documents.extractedData);
  
  // Get editableData from Redux so it's shared across components
  const editableDataFromRedux = useSelector(state => state.documents.editableData);
  
  // Get validation errors from Redux instead of local state
  const validationErrors = useSelector(state => state.validationErrors.errors);
  const isSpanishFromRedux = useSelector(state => state.validationErrors.isSpanish);
  
  // Get isDirty from Redux so it's shared across components
  const isDirty = useSelector(state => state.documents.isDirty);

  // Initialize isSpanish from cookie, default to true if not set
  const [isSpanish, setIsSpanish] = useLanguage();
  const [localEditableData, setLocalEditableData] = useState([]);
  
  // Store original data for cancel functionality
  const originalDataRef = useRef(null);
  const isFirstMount = useRef(true);
  
  // Use Redux editableData if available, otherwise use local state
  const editableData = editableDataFromRedux || localEditableData;

  // Store original extractedData to sessionStorage when it first appears or when it changes
  useEffect(() => {
    // On first mount (page load/refresh), don't set originalExtractedData
    // It should only be set when processing starts, not on refresh
    // BUT preserve it if we have a lastJobId (meaning there's a job we can restore)
    if (isFirstMount.current) {
      isFirstMount.current = false;
      const lastJobId = sessionStorage.getItem('lastJobId');
      // Only clear if there's no lastJobId - if we have a job ID, keep the data for restore
      if (!lastJobId) {
        sessionStorage.removeItem('originalExtractedData');
      } else {
      }
      return;
    }

    if (extractedData) {
      // Store the original extractedData in sessionStorage as baseline for cancel
      // This will be updated when user saves, but initially stores the first extractedData
      const stored = sessionStorage.getItem('originalExtractedData');
      const hasActiveJob = sessionStorage.getItem('activeJobId');
      const hasCompletedJob = sessionStorage.getItem('hasCompletedJob');
      const lastJobId = sessionStorage.getItem('lastJobId');
      
      // Only store if:
      // 1. Not already stored (first time)
      // 2. AND there's an active job or completed job (meaning we're in a processing session)
      // This prevents setting it on page refresh when there's no active processing
      if (!stored && (hasActiveJob || hasCompletedJob)) {
        // Only store if not already stored (first time) and we have an active/completed job
        const dataToStore = Array.isArray(extractedData) ? extractedData : [extractedData];
        if (dataToStore.length > 0) {
          sessionStorage.setItem('originalExtractedData', JSON.stringify(extractedData));
        }
      } else if (!stored && !hasActiveJob && !hasCompletedJob && !lastJobId) {
        // If no active/completed job AND no lastJobId, clear any stale data
        // But preserve it if lastJobId exists (meaning we can restore)
        sessionStorage.removeItem('originalExtractedData');
      }
      // If stored data exists and matches current extractedData, keep it (don't clear)
      // This handles the case where data was just restored
    } else {
      // Clear sessionStorage when extractedData is cleared (documents reset)
      // But only if there's no lastJobId (meaning no job to restore)
      const lastJobId = sessionStorage.getItem('lastJobId');
      if (!lastJobId) {
        sessionStorage.removeItem('originalExtractedData');
      }
    }
  }, [extractedData]);

  useEffect(() => {
    // Only initialize if we don't have editableData in Redux
    if (editableDataFromRedux) {
      // Validate editableDataFromRedux structure
      const isValidData = Array.isArray(editableDataFromRedux) 
        ? editableDataFromRedux.length > 0 && editableDataFromRedux.every(d => d && typeof d === 'object')
        : editableDataFromRedux && typeof editableDataFromRedux === 'object' && Object.keys(editableDataFromRedux).length > 0;
      
      if (isValidData) {
        // Store original data if not already stored
        if (!originalDataRef.current) {
          originalDataRef.current = JSON.parse(JSON.stringify(editableDataFromRedux));
          // Also ensure sessionStorage is set if it's not already
          const stored = sessionStorage.getItem('originalExtractedData');
          if (!stored && editableDataFromRedux) {
            const dataToCheck = Array.isArray(editableDataFromRedux) ? editableDataFromRedux : [editableDataFromRedux];
            if (dataToCheck.length > 0) {
              sessionStorage.setItem('originalExtractedData', JSON.stringify(editableDataFromRedux));
            }
          }
        }
        return; // Use existing Redux data
      } else {
        console.warn('editableDataFromRedux is invalid, falling back to extractedData/jsonData');
      }
    }
    
    // Try extractedData first, then jsonData, then sessionStorage as fallback
    let dataToUse = extractedData || jsonData;
    
    // Fallback: Check sessionStorage if dataToUse is null/empty
    if (!dataToUse || (Array.isArray(dataToUse) && dataToUse.length === 0) || (typeof dataToUse === 'object' && Object.keys(dataToUse).length === 0)) {
  
      const storedData = sessionStorage.getItem('originalExtractedData');
      if (storedData) {
        try {
          const parsedStored = JSON.parse(storedData);
          // Validate stored data
          const isValidStored = Array.isArray(parsedStored) 
            ? parsedStored.length > 0 && parsedStored.every(d => d && typeof d === 'object')
            : parsedStored && typeof parsedStored === 'object' && Object.keys(parsedStored).length > 0;
          
          if (isValidStored) {
            dataToUse = parsedStored;
          }
        } catch (e) {
          console.error('Error parsing stored data from sessionStorage:', e);
        }
      }
    }
    
    // Final validation: ensure dataToUse is valid before processing
    if (!dataToUse || (Array.isArray(dataToUse) && dataToUse.length === 0) || (typeof dataToUse === 'object' && Object.keys(dataToUse).length === 0)) {
      setLocalEditableData([]);
      dispatch(setEditableData([]));
      return;
    }
    const defaultFields = {
      completo: 1,
      version: 2,
      nombre_usuario_ultima_modificacion: "",
      es_plantilla: 0,
      pais_ubicacion: "ES",
      tipo_ubicacion: "B",
      localizacion: "",
      divisa: "900",
      cambio_divisa: "1,0",
      es_simplificado: 0,
      es_recapitulativo: 0,
      autorizacion_por_operacion: 0,
      estado_pdc: 0,
      e_mail: "",
      contacto_telefono: "",
      divisa_iso: "EUR",
      // Missing fields from PDF analysis - default values
      mrn: null, // Movement Reference Number
      fecha_operacion: null, // Operation Date
      clave_declarante: null, // Declarant Key
      lugar_entrega: null, // Delivery Place
      codigo_un: null, // UN Code for delivery place
      tipo_autorizacion_cd: null, // Type under 49 Autorización C.D.
      identificacion_adicional: null, // Additional Identification
      medio_transporte_frontera: null, // Transport Means
      documentos_transporte: [], // Transport Documents (N730, etc.)
      referencias_adicionales: [], // Additional References (Y024, etc.)
      informacion_adicional: [], // Additional Information (I5007, etc.)
      flag_datos_seguridad: 0, // Security Data Flag
    };
    let newData = [];
    if (Array.isArray(dataToUse)) {
      // Filter out null/undefined/empty objects and validate structure
      const validDeclarations = dataToUse.filter(d => 
        d !== null && 
        d !== undefined && 
        typeof d === 'object' && 
        Object.keys(d).length > 0
      );
      
      if (validDeclarations.length > 0) {
        newData = validDeclarations.map(declaration => ({
          ...defaultFields,
          ...declaration,
        }));
        setLocalEditableData(newData);
        dispatch(setEditableData(newData));
      } else {
        setLocalEditableData([]);
        dispatch(setEditableData([]));
      }
    } else if (dataToUse && typeof dataToUse === 'object' && Object.keys(dataToUse).length > 0) {
      newData = [{
        ...defaultFields,
        ...dataToUse,
      }];
      setLocalEditableData(newData);
      dispatch(setEditableData(newData));
    } else {
      console.warn('dataToUse is not a valid object or array');
      setLocalEditableData([]);
      dispatch(setEditableData([]));
    }
    
    // Store original data when first loaded (only if we have valid data)
    if (!originalDataRef.current && newData.length > 0) {
      originalDataRef.current = JSON.parse(JSON.stringify(newData));
    }
  }, [jsonData, extractedData, editableDataFromRedux, dispatch]);

  // Sync local isSpanish with Redux when validation errors exist
  useEffect(() => {
    if (validationErrors && validationErrors.length > 0 && isSpanishFromRedux !== undefined) {
      setIsSpanish(isSpanishFromRedux);
    }
  }, [isSpanishFromRedux, validationErrors]);

  // Automatically validate and log whenever data changes
  useEffect(() => {
    if (editableData && editableData.length > 0) {
      const errors = validateRequiredFields();
      
      // Dispatch errors to Redux so the sidebar can show them
      if (errors.length > 0) {
        dispatch(setValidationErrors(errors, isSpanish));
      } else {
        // Clear validation errors when all fields are valid
        dispatch(clearValidationErrors());
      }
    }
  }, [editableData, isSpanish, dispatch]);

  const handleChange = (declarationIndex, field, value) => {
    // Validate editableData exists and is valid
    if (!editableData || !Array.isArray(editableData) || editableData.length === 0) {
      console.warn('handleChange: editableData is invalid, cannot update');
      return;
    }
    
    // Validate declarationIndex is within bounds
    if (declarationIndex < 0 || declarationIndex >= editableData.length) {
      console.warn(`handleChange: declarationIndex ${declarationIndex} is out of bounds (length: ${editableData.length})`);
      return;
    }
    
    // Validate declaration exists
    if (!editableData[declarationIndex] || typeof editableData[declarationIndex] !== 'object') {
      console.warn(`handleChange: declaration at index ${declarationIndex} is invalid`);
      return;
    }
    
    try {
      // Deep clone to avoid mutating original data
      const updatedData = JSON.parse(JSON.stringify(editableData));
      updatedData[declarationIndex][field] = value;
      setLocalEditableData(updatedData);
      dispatch(setEditableData(updatedData));
      // Don't update extractedData here - only update on save
      dispatch(setDeclarationDirty(true));
      
      // Clear validation errors when user starts editing
      if (validationErrors && validationErrors.length > 0) {
        dispatch(clearValidationErrors());
      }
    } catch (error) {
      console.error('Error in handleChange:', error);
    }
  };

  const validateRequiredFields = () => {
    const errors = [];
    
    // Validate editableData exists and is an array
    if (!editableData || !Array.isArray(editableData) || editableData.length === 0) {
      return errors; // Return empty errors if no data to validate
    }
  
    editableData.forEach((declaration, declarationIndex) => {
      // Validate declaration structure
      if (!declaration || typeof declaration !== 'object') {
        console.warn(`validateRequiredFields: Invalid declaration at index ${declarationIndex}`);
        return; // Skip this declaration
      }
      const declarationErrors = [];
      const itemErrors = [];
      const packageErrors = [];
      const documentErrors = [];
  
      // Check declaration required fields
      if (!declaration.expediente) {
        declarationErrors.push({
          field: `expediente_${declarationIndex + 1}`,
          error: !isSpanish ? "Declaration" : "Expediente"
        });
      }
      if (!declaration.tipo) {
        declarationErrors.push({
          field: `tipo_${declarationIndex + 1}`,
          error: !isSpanish ? "Type" : "Tipo"
        });
      }
      // Clave Declarante is CRITICAL for export declarations
      if (!declaration.clave_declarante) {
        declarationErrors.push({
          field: `clave_declarante_${declarationIndex + 1}`,
          error: !isSpanish ? "Declarant Key" : "Clave Declarante"
        });
      }
      // Phone number of declarant is CRITICAL
      if (!declaration.contacto_telefono) {
        declarationErrors.push({
          field: `contacto_telefono_${declarationIndex + 1}`,
          error: !isSpanish ? "Phone Number" : "Teléfono"
        });
      }
      // Security Data Flag is CRITICAL (must be 0 or 1, not null)
      if (declaration.flag_datos_seguridad === null || declaration.flag_datos_seguridad === undefined) {
        declarationErrors.push({
          field: `flag_datos_seguridad_${declarationIndex + 1}`,
          error: !isSpanish ? "Security Data Flag" : "Datos de Seguridad"
        });
      }
  
      // Check items required fields - validate partidas array
      if (!declaration.partidas || !Array.isArray(declaration.partidas)) {
        // No partidas to validate, skip
        return;
      }
      
      declaration.partidas.forEach((item, itemIndex) => {
        // Validate item structure
        if (!item || typeof item !== 'object') {
          console.warn(`validateRequiredFields: Invalid item at index ${itemIndex} in declaration ${declarationIndex}`);
          return; // Skip this item
        }
        
        const currentItemErrors = [];
  
        if (!item.descripcion_mercancia) {
          currentItemErrors.push({
            field: `descripcion_mercancia_${declarationIndex + 1}_${itemIndex}`,
            error: !isSpanish ? "Goods Description" : "Descripción de Mercancía"
          });
        }
        if (!item.codigo_nc) {
          currentItemErrors.push({
            field: `codigo_nc_${declarationIndex + 1}_${itemIndex}`,
            error: !isSpanish ? "NC Code" : "Código NC"
          });
        }
        if (!item.masa_bruta) {
          currentItemErrors.push({
            field: `masa_bruta_${declarationIndex + 1}_${itemIndex}`,
            error: !isSpanish ? "Gross Weight" : "Masa Bruta"
          });
        }
        if (!item.masa_neta) {
          currentItemErrors.push({
            field: `masa_neta_${declarationIndex + 1}_${itemIndex}`,
            error: !isSpanish ? "Net Weight" : "Masa Neta"
          });
        }
        if (!item.pais_origen) {
          currentItemErrors.push({
            field: `pais_origen_${declarationIndex + 1}_${itemIndex}`,
            error: !isSpanish ? "Country of Origin" : "País de Origen"
          });
        }

        if (currentItemErrors.length > 0) {
          itemErrors.push({ 
            title: !isSpanish ? `Item #${itemIndex + 1}` : `Partida #${itemIndex + 1}`, 
            errors: currentItemErrors 
          });
        }
  
        // Check packages required fields - organized by package
        // Validate embalajes array
        if (!item.embalajes || !Array.isArray(item.embalajes)) {
          // No embalajes to validate, skip
          return;
        }
        
        item.embalajes.forEach((embalaje, embalajeIndex) => {
          // Validate embalaje structure
          if (!embalaje || typeof embalaje !== 'object') {
            console.warn(`validateRequiredFields: Invalid embalaje at index ${embalajeIndex} in item ${itemIndex}`);
            return; // Skip this embalaje
          }
          
          const currentPackageErrors = [];
          
          if (!embalaje.tipo) {
            currentPackageErrors.push({
              field: `packages_tipo_${declarationIndex}_${itemIndex}_${embalajeIndex}`,
              error: !isSpanish ? "Type" : "Tipo"
            });
          }
          if (!embalaje.es_interno_o_externo) {
            currentPackageErrors.push({
              field: `packages_es_interno_o_externo_${declarationIndex}_${itemIndex}_${embalajeIndex}`,
              error: !isSpanish ? "Internal or External" : "Es Interno o Externo"
            });
          }

          if (!embalaje.cantidad) {
            currentPackageErrors.push({
              field: `packages_cantidad_${declarationIndex}_${itemIndex}_${embalajeIndex}`,
              error: !isSpanish ? "Quantity" : "Cantidad"
            });
          }
          
          // Validate marcas array
          if (!embalaje.marcas || !Array.isArray(embalaje.marcas)) {
            // No marcas to validate, skip
            return;
          }
          
          embalaje.marcas.forEach((marca, marcaIndex) => {
            // Validate marca structure
            if (!marca || typeof marca !== 'object') {
              console.warn(`validateRequiredFields: Invalid marca at index ${marcaIndex}`);
              return; // Skip this marca
            }
            
            if (!marca.marca) {
              currentPackageErrors.push({
                field: `packages_marcas_marca_${declarationIndex}_${itemIndex}_${embalajeIndex}_${marcaIndex}`,
                error: !isSpanish ? `Mark #${marcaIndex + 1}` : `Marca #${marcaIndex + 1}`
              });
            }
          });

          // Add package errors if any exist
          if (currentPackageErrors.length > 0) {
            packageErrors.push({
              itemTitle: !isSpanish ? `Item #${itemIndex + 1}` : `Partida #${itemIndex + 1}`,
              packageTitle: !isSpanish ? `Package #${embalajeIndex + 1}` : `Embalaje #${embalajeIndex + 1}`,
              errors: currentPackageErrors
            });
          }
        });
  
        // Check documents required fields - organized by document
        // Validate documentos array
        if (!item.documentos || !Array.isArray(item.documentos)) {
          // No documentos to validate, skip
          return;
        }
        
        item.documentos.forEach((documento, docIndex) => {
          // Validate documento structure
          if (!documento || typeof documento !== 'object') {
            console.warn(`validateRequiredFields: Invalid documento at index ${docIndex} in item ${itemIndex}`);
            return; // Skip this documento
          }
          
          const currentDocumentErrors = [];
          
          if (!documento.codigo_documento) {
            currentDocumentErrors.push({
              field: `documents_codigo_documento_${declarationIndex}_${itemIndex}_${docIndex}`,
              error: !isSpanish ? "Document Code" : "Código de Documento"
            });
          }
          if (!documento.tipo) {
            currentDocumentErrors.push({
              field: `documents_tipo_${declarationIndex}_${itemIndex}_${docIndex}`,
              error: !isSpanish ? "Type" : "Tipo"
            });
          }

          // Add document errors if any exist
          if (currentDocumentErrors.length > 0) {
            documentErrors.push({
              itemTitle: !isSpanish ? `Item #${itemIndex + 1}` : `Partida #${itemIndex + 1}`,
              documentTitle: !isSpanish ? `Document #${docIndex + 1}` : `Documento #${docIndex + 1}`,
              errors: currentDocumentErrors
            });
          }
        });
      });
  
      // Add all errors for this declaration
      if (declarationErrors.length > 0 || itemErrors.length > 0 || packageErrors.length > 0 || documentErrors.length > 0) {
        errors.push({
          declarationNumber: declarationIndex + 1,
          declarationErrors,
          itemErrors,
          packageErrors,
          documentErrors
        });
      }
    });
  
    return errors;
  };

  const saveChanges = () => {
    // Get the latest editableData from Redux to ensure we're saving the most current data
    const currentEditableData = editableDataFromRedux || editableData;
    
    // Validate data before saving
    if (!currentEditableData) {
      console.error('saveChanges: currentEditableData is null/undefined');
      return { success: false, errors: [{ message: 'No data to save' }] };
    }
    
    const isValidData = Array.isArray(currentEditableData) 
      ? currentEditableData.length > 0 && currentEditableData.every(d => d && typeof d === 'object')
      : currentEditableData && typeof currentEditableData === 'object' && Object.keys(currentEditableData).length > 0;
    
    if (!isValidData) {
      console.error('saveChanges: currentEditableData is invalid or empty');
      return { success: false, errors: [{ message: 'Invalid or empty data structure' }] };
    }
    
    const errors = validateRequiredFields();
    
    if (errors.length > 0) {
      dispatch(setValidationErrors(errors, isSpanish));
      return { success: false, errors };
    }
    
    try {
      dispatch(clearValidationErrors());
      dispatch(setExtractedData(currentEditableData));
      dispatch(setDeclarationDirty(false));
      // Update original data ref to the saved state so cancel will reset to this point
      originalDataRef.current = JSON.parse(JSON.stringify(currentEditableData));
      // Also update sessionStorage so the saved state becomes the new baseline
      sessionStorage.setItem('originalExtractedData', JSON.stringify(currentEditableData));
      return { success: true };
    } catch (error) {
      console.error('Error saving changes:', error);
      return { success: false, errors: [{ message: 'Error saving changes: ' + error.message }] };
    }
  };

  const cancelChanges = () => {
    // ALWAYS prioritize sessionStorage first - it's the most reliable source
    let resetData = null;
    const storedOriginalData = sessionStorage.getItem('originalExtractedData');
    

    if (storedOriginalData) {
      try {
        const parsed = JSON.parse(storedOriginalData);
        // Validate parsed data structure
        const isValid = Array.isArray(parsed) 
          ? parsed.length > 0 && parsed.every(d => d && typeof d === 'object')
          : parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
        
        if (isValid) {
          if (Array.isArray(parsed) && parsed.length > 0) {
            resetData = parsed;
          } else if (parsed && !Array.isArray(parsed)) {
            resetData = parsed;
          }
        } else {
          console.warn('cancelChanges: Stored data from sessionStorage is invalid');
        }
      } catch (e) {
        console.error('Error parsing storedOriginalData from sessionStorage:', e);
      }
    }
    
    // Priority 2: Use originalDataRef if it has valid data AND sessionStorage didn't work
    if (!resetData && originalDataRef.current) {
      const refData = originalDataRef.current;
      
      // Validate refData structure
      const isValidRef = Array.isArray(refData) 
        ? refData.length > 0 && refData.every(d => d && typeof d === 'object')
        : refData && typeof refData === 'object' && Object.keys(refData).length > 0;
      
      if (isValidRef) {
        try {
          if (Array.isArray(refData) && refData.length > 0) {
            resetData = JSON.parse(JSON.stringify(refData));
          } else if (refData && !Array.isArray(refData)) {
            resetData = JSON.parse(JSON.stringify(refData));
          }
        } catch (e) {
          console.error('Error cloning originalDataRef:', e);
        }
      }
    }
    
    // Priority 3: Fallback to extractedData or jsonData
    if (!resetData) {
      if (extractedData) {
        const isValidExtracted = Array.isArray(extractedData) 
          ? extractedData.length > 0 && extractedData.every(d => d && typeof d === 'object')
          : extractedData && typeof extractedData === 'object' && Object.keys(extractedData).length > 0;
        
        if (isValidExtracted) {
          try {
            resetData = JSON.parse(JSON.stringify(extractedData));
          } catch (e) {
            console.error('Error cloning extractedData:', e);
          }
        }
      } 
      
      if (!resetData && jsonData) {
        const isValidJson = Array.isArray(jsonData) 
          ? jsonData.length > 0 && jsonData.every(d => d && typeof d === 'object')
          : jsonData && typeof jsonData === 'object' && Object.keys(jsonData).length > 0;
        
        if (isValidJson) {
          try {
            resetData = JSON.parse(JSON.stringify(jsonData));
          } catch (e) {
            console.error('Error cloning jsonData:', e);
          }
        }
      }
    }
    
    // Only update if we have valid data - don't clear if everything is null/empty
    if (resetData !== null) {
      setLocalEditableData(resetData);
      dispatch(setEditableData(resetData));
      // Also update extractedData to match the reset state
      dispatch(setExtractedData(resetData));
      dispatch(setDeclarationDirty(false));
      // Clear validation errors when canceling
      dispatch(clearValidationErrors());
      
      // Restore previous template if it exists, otherwise clear it
      try {
        const previousTemplateStr = sessionStorage.getItem('previousSelectedTemplate');
        if (previousTemplateStr) {
          const previousTemplate = JSON.parse(previousTemplateStr);
          if (previousTemplate) {
            // Restore the previous template
            dispatch(setSelectedTemplate(previousTemplate));
          } else {
            // Previous was null, so clear the template
            dispatch(clearSelectedTemplate());
          }
          // Clear the stored previous template after using it
          sessionStorage.removeItem('previousSelectedTemplate');
        } else {
          // No previous template stored, clear current template
          dispatch(clearSelectedTemplate());
        }
      } catch (e) {
        dispatch(clearSelectedTemplate());
      }
    } else {
      // If we have no data to reset to, just clear the dirty flag
      dispatch(setDeclarationDirty(false));
      dispatch(clearValidationErrors());
      
      // Restore previous template if it exists, otherwise clear it
      try {
        const previousTemplateStr = sessionStorage.getItem('previousSelectedTemplate');
        if (previousTemplateStr) {
          const previousTemplate = JSON.parse(previousTemplateStr);
          if (previousTemplate) {
            dispatch(setSelectedTemplate(previousTemplate));
          } else {
            dispatch(clearSelectedTemplate());
          }
          sessionStorage.removeItem('previousSelectedTemplate');
        } else {
          dispatch(clearSelectedTemplate());
        }
      } catch (e) {
        dispatch(clearSelectedTemplate());
      }
    }
  };

  const countTotalItems = () => {
    if (Array.isArray(editableData)) {
      return editableData.reduce((total, declaration) => {
        return total + (declaration.partidas?.length || 0);
      }, 0);
    }
    return 0;
  };

  // Helper to update both local and Redux state
  const updateEditableData = (newData) => {
    setLocalEditableData(newData);
    dispatch(setEditableData(newData));
  };

  return {
    isSpanish,
    editableData,
    setEditableData: updateEditableData, // Use the helper that updates both local and Redux
    isDirty, // Now comes from Redux
    setIsDirty: (value) => dispatch(setDeclarationDirty(value)), // Helper to set isDirty in Redux
    validationErrors, // This now comes from Redux
    handleChange,
    saveChanges,
    cancelChanges,
    countTotalItems
  };
};